import type { StatsResponse } from '../../api/types';
import type { HealthResponse } from '../../api/types';

interface MetricsStripProps {
  stats: StatsResponse | null;
  health: HealthResponse | null;
  onNavigate: (hash: string) => void;
}

function SvgIcon({ path, className }: { path: string; className?: string }) {
  return (
    <svg
      class={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.5"
      stroke-linecap="round"
      stroke-linejoin="round"
      dangerouslySetInnerHTML={{ __html: path }}
    />
  );
}

const ICONS = {
  products: '<path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/>',
  ingredients: '<path d="M10 2v7.53a2 2 0 0 1-.2.9L4.7 20.5a1 1 0 0 0 .9 1.5h12.8a1 1 0 0 0 .9-1.5l-5.1-10.07a2 2 0 0 1-.2-.9V2"/><path d="M8.5 2h7"/><path d="M7 16.5h10"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  scans: '<path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/><path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/><line x1="7" y1="12" x2="17" y2="12"/>',
  verdicts: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  health: '<path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"/>',
};

export function MetricsStrip({ stats, health, onNavigate }: MetricsStripProps) {
  const s = stats;
  const degradedCount = health
    ? Object.values(health.dependencies).filter((v) => v !== 'ok').length
    : 0;
  const healthStatus = health
    ? (degradedCount === 0 ? 'ok' : 'degraded')
    : 'unknown';

  const missingNames = s ? Math.max(0, s.totalProducts - (s.totalProducts /* approximation */)) : 0;

  return (
    <div class="metrics-strip">
      {/* Products */}
      <div class="metrics-card" onClick={() => onNavigate('catalog/products')}>
        <SvgIcon path={ICONS.products} className="metrics-card-icon" />
        <div class="metrics-card-label">Products</div>
        <div class="metrics-card-value">{s ? s.totalProducts.toLocaleString() : '\u2014'}</div>
        {s && s.totalProducts > 0 && (
          <div class="metrics-card-subtitle">Managed catalog</div>
        )}
      </div>

      {/* Ingredients */}
      <div class="metrics-card" onClick={() => onNavigate('catalog/ingredients')}>
        <SvgIcon path={ICONS.ingredients} className="metrics-card-icon" />
        <div class="metrics-card-label">Ingredients</div>
        <div class="metrics-card-value">{s ? s.totalIngredients.toLocaleString() : '\u2014'}</div>
        {s && s.autoLearnedIngredients > 0 && (
          <div class="metrics-card-subtitle warn">{s.autoLearnedIngredients} auto-learned</div>
        )}
      </div>

      {/* Users */}
      <div class="metrics-card" onClick={() => onNavigate('catalog/users')}>
        <SvgIcon path={ICONS.users} className="metrics-card-icon" />
        <div class="metrics-card-label">Users</div>
        <div class="metrics-card-value">{s ? s.totalUsers.toLocaleString() : '\u2014'}</div>
        <div class="metrics-card-subtitle">Active accounts</div>
      </div>

      {/* Scans */}
      <div class="metrics-card" onClick={() => onNavigate('catalog/products')}>
        <SvgIcon path={ICONS.scans} className="metrics-card-icon" />
        <div class="metrics-card-label">Scans</div>
        <div class="metrics-card-value">{s ? s.totalScans.toLocaleString() : '\u2014'}</div>
        <div class="metrics-card-subtitle">Total scans</div>
      </div>

      {/* Verdicts */}
      <div class="metrics-card" onClick={() => onNavigate('catalog/verdicts')}>
        <SvgIcon path={ICONS.verdicts} className="metrics-card-icon" />
        <div class="metrics-card-label">Verdicts</div>
        <div class="metrics-card-value">{s ? (s.totalVerdicts || 0).toLocaleString() : '\u2014'}</div>
        <div class="metrics-card-subtitle">Framework evaluations</div>
      </div>

      {/* Health */}
      <div class="metrics-card" onClick={() => onNavigate('ops/health')}>
        <SvgIcon path={ICONS.health} className="metrics-card-icon" />
        <div class="metrics-card-label">Health</div>
        <div class="metrics-card-value">
          <span class={`metrics-health-dot ${healthStatus}`} />
          {healthStatus === 'ok' ? 'OK' : healthStatus === 'degraded' ? `${degradedCount}` : '\u2014'}
        </div>
        <div class="metrics-card-subtitle">
          {healthStatus === 'ok'
            ? 'All systems go'
            : healthStatus === 'degraded'
              ? `${degradedCount} degraded`
              : 'Checking...'}
        </div>
      </div>
    </div>
  );
}
